module.exports = {
	"data": {
		"markdownRemark": {
			"html": "<p>There are multiple types of clients that can walk across the API. Earlier solutions were concerned with different devices and languages (eg mobile vs desktop, browser vs native, React vs Vue) and what could be termed GUI-to-machine. In moving forward, hypermedia also aids the additional concerns of machine-to-machine and application-to-api concerns. The underlying design in this approach is how can the client reconstruct state and get the server to converge on a new state, whilst at the same time not require human interpretation/intervention?</p>\n<p><strong>How to Hypermedia</strong> has code samples for three different types of clients. This chapter gives context of how each of these clients are different. </p>\n<p><img src=\"/hypermedia/advanced/multiple-clients.png\" alt=\"multiple clients\"></p>\n<h3>Summary of clients</h3>\n<ul>\n<li><strong>Single resource:</strong> the client requests resources as needed and discards (independent representations across requests or between routes/views/controllers)</li>\n<li><strong>Combination resource:</strong> the client requests resources and caches allowing for rich processing across requests (eg single-page application with cache across routes/views)</li>\n<li><strong>Resource processing:</strong> the clients needs to perform business processes with a high level of automation (cache + synchronisation across multiple requests)</li>\n</ul>\n<h3>Client one: single resource</h3>\n<p>In the samples, this is the general purpose client that is used to view the API itself. It is code that can make a single request/response display cycle. Primarily all it needs to be able to do is know mime type to convert it into a in-memory representation and find the <code>links</code> and then bind it to a generalised HTML view. It doesn’t tend to scale well, nor does it need to. This example is the API browser code that can be shipped on every HTML representation of a resource.</p>\n<p><img src=\"/hypermedia/advanced/single-resource.png\" alt=\"resource\"></p>\n<h3>Client two: combination resource</h3>\n<p>In the samples, this is the ‘todo’ app with its rich interface and easy editing. The code makes many requests to build up a cache of representations that are then bound to the user interface. This approach can then streamline the user experience across a business process.</p>\n<p><img src=\"/hypermedia/advanced/combination-resource.png\" alt=\"resource\"></p>\n<h3>Client three: processing resources</h3>\n<p>In the samples, this is the <strong>admin</strong> function inside the todo app. The goal of the admin code is the clone or update entire data structures without intervention. The code is able to synchronise an entire network using only link relations (which hints for depth and width processing). In practice, this is used in multiple ways: as an export function, an bulk-processing update or to move data across environments. The purpose of this type of client is to perform bulk processing of the resources on the client (not on the server) without the need to batch process.</p>\n<p><img src=\"/hypermedia/advanced/processing-resource.png\" alt=\"resource\"></p>",
			"fields": {
				"slug": "/advanced/0-multiple-clients/"
			},
			"excerpt": "There are multiple types of clients that can walk across the API. Earlier solutions were concerned with different devices and languages (eg...",
			"frontmatter": {
				"title": "Multiple clients",
				"videoId": "hE6ymKeJmsM",
				"videoAuthor": null,
				"parent": "/Users/todd/src/howtohypermedia/content/hypermedia/advanced/0-multiple-clients.md absPath of file",
				"question": "What types of clients are likely to be already running against any API?",
				"answers": [
					"Mobile vs browser",
					"Machine-to-machine and GUI-to-machine",
					"People-to-data and application-to-api",
					"All of the above"
				],
				"correctAnswer": 3,
				"description": "Learn that most APIs should have multiple types of clients.",
				"pageTitle": "Writing Clients Overview Tutorial"
			}
		},
		"mds": {
			"edges": [
				{
					"node": {
						"frontmatter": {
							"title": "Get started with your favorite technology",
							"description": null
						},
						"fields": {
							"slug": "/choose/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Update Collection Resource",
							"description": "Learn the code mechanics of a edit forms on a collection resource that uses different mime-types"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/11-update-collection/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "It was great having you!",
							"description": null
						},
						"fields": {
							"slug": "/success/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Update Singleton Resource",
							"description": "Learn the code mechanics of a edit forms on a singleton resource"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/10-update-singleton/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a hypermedia API that works within the constraints of REST"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to build a hypermedia API "
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "",
							"description": null
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/2-modelling-representations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Modelling Domain",
							"description": "Learn how to diagram the API textually and get also get visual output"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/3-modelling-domain-link-relations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Constructing Representations",
							"description": "Learn the code mechanics of request/response of representation in an MVC framework with the root API representation containing links and a version"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/4-constructing-representations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Constructing Collections",
							"description": "Learn the code mechanics of a singleton resource with the user resource"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/6-constructing-collection/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Constructing Searchable Resources",
							"description": "Learn the code mechanics of a search forms on a collection"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/8-constructing-search-resources/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Constructing Singletons",
							"description": "Learn the code mechanics of a singleton resource with the user resource"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/5-constructing-singleton/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Create Resource",
							"description": "Learn the code mechanics of a create forms on a collection"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/9-create-resource/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Constructing Virtual Resource",
							"description": "Learn the code mechanics of a virtual resource with the user resource"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/7-constructing-viritual/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a todo app and admin functions over the top of a hypermedia API"
						},
						"fields": {
							"slug": "/vue-todo-app/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Client-side URLs and routing",
							"description": "Design your application around the holding and reconstructing client state through the URI in the browser location requires matching client views and the data it requires"
						},
						"fields": {
							"slug": "/vue-todo-app/2-client-side-urls-and-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting started",
							"description": "Learn how to build a todo client to use the hypermedia API "
						},
						"fields": {
							"slug": "/vue-todo-app/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Client-side application cache",
							"description": "Client need to have one copy of representations in the application cache"
						},
						"fields": {
							"slug": "/vue-todo-app/3-application-cache/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Updates and deletes",
							"description": "The application cache also deals with the updates and deletion of resources"
						},
						"fields": {
							"slug": "/vue-todo-app/4-updates-and-deletes/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "On-demand Authentication",
							"description": "Be able to create and renew authentication tokens when the server requests valid authentication tokens"
						},
						"fields": {
							"slug": "/vue-todo-app/5-on-demand-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "Learn how to build a todo app over the top of a hypermedia API"
						},
						"fields": {
							"slug": "/vue-todo-app/7-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to build a general purpose client to browse over the API "
						},
						"fields": {
							"slug": "/vue-api-browser/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Cloning Resources",
							"description": "Forms in hypermedia make cloning of trees straightforward and we can leverage that with drag and drop functionality alongside the client-side application cache"
						},
						"fields": {
							"slug": "/vue-todo-app/6-cloning-resources/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "HTML representation",
							"description": "Learn what and why to build HTML representation of data with code on demand"
						},
						"fields": {
							"slug": "/vue-api-browser/2-build-html-representation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a general purpose client to browse over the API "
						},
						"fields": {
							"slug": "/vue-api-browser/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Generate HTML",
							"description": "Learn how to respond as HTML in ASP.NET Core"
						},
						"fields": {
							"slug": "/vue-api-browser/3-build-html-content-negotiation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Register text/html",
							"description": "Learn how to respond to HTML content negotiation in ASP.NET Core"
						},
						"fields": {
							"slug": "/vue-api-browser/4-register-html-content-negotiation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "GET application/json",
							"description": "Learn how to request JSON from the client"
						},
						"fields": {
							"slug": "/vue-api-browser/5-request-json/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "Learn how to user hypermedia as engine to navigate an API and make changes"
						},
						"fields": {
							"slug": "/vue-api-browser/10-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Displaying with interaction",
							"description": "Learn how to process JSON to make delete, create and update"
						},
						"fields": {
							"slug": "/vue-api-browser/6-displaying-with-interaction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Displaying forms",
							"description": "Learn how to process form values for create and edit forms"
						},
						"fields": {
							"slug": "/vue-api-browser/8-displaying-form-values/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Deleting a resource",
							"description": "Learn how to process JSON to try and delete a resource"
						},
						"fields": {
							"slug": "/vue-api-browser/7-try-delete-representation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn all about hypermedia and why a REST-style of architecture is simple and subtle. It's not really for the lazy or those who like to be clever."
						},
						"fields": {
							"slug": "/basics/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Minimally layered design",
							"description": "Learn why hypermedia is a thin layer across persistence that should guide us through the business problem."
						},
						"fields": {
							"slug": "/basics/1-minimally-layered-design/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Submitting forms",
							"description": "Learn how to process creates (POST) and updates (PUT) on a resource"
						},
						"fields": {
							"slug": "/vue-api-browser/9-submitting-forms/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Big Picture (Trends)",
							"description": "Learn that distributed applications have trended towards data and business logic being processed at the client edge"
						},
						"fields": {
							"slug": "/basics/3-big-picture/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Hypermedia for the dedicated",
							"description": "Learn some techniques that will ease the server and client development"
						},
						"fields": {
							"slug": "/basics/4-for-the-dedicated/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Navigating a hypermedia API",
							"description": "Learn what a hypermedia looks like to be able to explore a domain."
						},
						"fields": {
							"slug": "/basics/2-navigating-api/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Multiple clients",
							"description": "Learn that most APIs should have multiple types of clients."
						},
						"fields": {
							"slug": "/advanced/0-multiple-clients/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "API Modelling",
							"description": "Learn about how collections and singletons are modelled in hypermedia"
						},
						"fields": {
							"slug": "/advanced/1-api-modelling/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Forms Modelling",
							"description": "Links and Forms are used to update resources and controlled by the server that the client then uses"
						},
						"fields": {
							"slug": "/advanced/2-forms-modelling/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Common Questions",
							"description": "Answer common questions about these tutorials"
						},
						"fields": {
							"slug": "/advanced/4-common-questions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Todo Domain",
							"description": "Learn about domain of the API"
						},
						"fields": {
							"slug": "/advanced/3-todo-domain/"
						}
					}
				}
			]
		}
	},
	"pathContext": {
		"slug": "/advanced/0-multiple-clients/"
	}
};


//////////////////
// WEBPACK FOOTER
// ./~/json-loader!./.cache/json/advanced-0-multiple-clients.json
// module id = ./node_modules/json-loader/index.js!./.cache/json/advanced-0-multiple-clients.json
// module chunks = 46